// src/main.js
import { createApp } from 'vue';
import App from './App.vue';

import { createPinia } from 'pinia';
import piniaPluginPersistedstate from 'pinia-plugin-persistedstate';
import { useUserInfoStore } from '@/stores/userInfo';
import { loadRuntimeConfig } from './runtimeConfig';

import 'mdb-vue-ui-kit/css/mdb.min.css';
import '@/../public/fonts/inter/inter-v18-latin-regular.woff2';
import './assets/styles/fonts.less';
import '@fortawesome/fontawesome-free/css/all.min.css';

import {
    MDBAlert, MDBCol, MDBRow, MDBTextarea, MDBInput, MDBContainer,
    MDBCard, MDBCardTitle, MDBCardText, MDBCheckbox, MDBIcon, MDBBtn,
    MDBSelect, MDBFile, MDBModal, MDBModalHeader, MDBModalTitle, MDBModalBody,
    MDBModalFooter, MDBRadio, MDBAccordion, MDBAccordionItem, MDBTransition,
    MDBSpinner, MDBSwitch
} from 'mdb-vue-ui-kit';

import { buildRouter } from './router';

// Pinia setup
const pinia = createPinia();
pinia.use(piniaPluginPersistedstate);

// Create the app
const app = createApp(App);

// Register MDB components globally
app.component('MDBAlert', MDBAlert);
app.component('MDBCol', MDBCol);
app.component('MDBTextarea', MDBTextarea);
app.component('MDBInput', MDBInput);
app.component('MDBContainer', MDBContainer);
app.component('MDBCard', MDBCard);
app.component('MDBCardTitle', MDBCardTitle);
app.component('MDBCardText', MDBCardText);
app.component('MDBCheckbox', MDBCheckbox);
app.component('MDBIcon', MDBIcon);
app.component('MDBBtn', MDBBtn);
app.component('MDBSelect', MDBSelect);
app.component('MDBFile', MDBFile);
app.component('MDBModal', MDBModal);
app.component('MDBModalHeader', MDBModalHeader);
app.component('MDBModalTitle', MDBModalTitle);
app.component('MDBModalBody', MDBModalBody);
app.component('MDBModalFooter', MDBModalFooter);
app.component('MDBRadio', MDBRadio);
app.component('MDBAccordion', MDBAccordion);
app.component('MDBAccordionItem', MDBAccordionItem);
app.component('MDBTransition', MDBTransition);
app.component('MDBSpinner', MDBSpinner);
app.component('MDBRow', MDBRow);
app.component('MDBSwitch', MDBSwitch);

// Use plugins
app.use(pinia);

/**
 * Normalize base path:
 * - root => ''
 * - otherwise => '/foo' (single leading slash, no trailing slash)
 */
const normalizeBasePath = (s) => {
    const raw = String(s ?? '').trim();
    if (!raw || raw === '/') return '';
    const name = raw.replace(/^\/+|\/+$/g, '');
    return name ? `/${name}` : '';
};

/**
 * Join URL path segments safely under BASE_PATH (never produces //something).
 * Examples:
 *   BASE_PATH ''     + ('register') => '/register'
 *   BASE_PATH '/wysh'+ ('register') => '/wysh/register'
 */
const makePathJoiner = (basePath) => (...segs) => {
    const prefix = normalizeBasePath(basePath); // ensures '' or '/foo'
    const cleaned = segs
        .filter(s => s != null && s !== '')
        .map(s => String(s).replace(/^\/+|\/+$/g, ''));

    const tail = cleaned.join('/');
    if (!tail) return prefix || '/';
    return (prefix || '') + '/' + tail;
};

// ---- Boot sequence: load .env → load runtime config → mount ----
(async () => {
    // Read baked Vue CLI env vars (compile-time)
    const {
        VUE_APP_API_DOMAIN,
        VUE_APP_API_ROOT,
        VUE_APP_REDCAP_API_DOMAIN,
        VUE_APP_BASE_URL,
        VUE_APP_BASE_PATH
    } = process.env;

    // Derive a normalized base path from env vars
    let basePath = VUE_APP_BASE_PATH;
    if ((!basePath || basePath === '/') && VUE_APP_BASE_URL) {
        try {
            basePath = new URL(VUE_APP_BASE_URL).pathname;
        } catch {
            basePath = '';
        }
    }
    basePath = normalizeBasePath(basePath);

    // Build the initial config object (from baked envs)
    let cfg = {
        API_DOMAIN: VUE_APP_API_DOMAIN || '',
        API_ROOT: VUE_APP_API_ROOT || '',
        REDCAP_API_DOMAIN: VUE_APP_REDCAP_API_DOMAIN || '',
        BASE_PATH: basePath,
        BASE_URL: VUE_APP_BASE_URL || (window.location.origin + (basePath || ''))
    };

    // Try to load and merge runtime config (if present)
    try {
        const runtimeCfg = await loadRuntimeConfig(); // /config/config.json
        cfg = { ...cfg, ...runtimeCfg };

        // Normalize runtime-provided BASE_PATH too
        cfg.BASE_PATH = normalizeBasePath(cfg.BASE_PATH);

        if (!cfg.BASE_URL && typeof cfg.BASE_PATH === 'string') {
            cfg.BASE_URL = window.location.origin + (cfg.BASE_PATH || '');
        }

        console.log('[Runtime Config Loaded]', cfg);
    } catch (err) {
        console.warn('[Runtime Config] Not found or failed to load:', err?.message || err);
    }

    // Back-compat aliases (for legacy code)
    cfg.VUE_APP_API_DOMAIN = cfg.API_DOMAIN;
    cfg.VUE_APP_API_ROOT = cfg.API_ROOT;
    cfg.VUE_APP_REDCAP_API_DOMAIN = cfg.REDCAP_API_DOMAIN;
    cfg.VUE_APP_BASE_PATH = cfg.BASE_PATH; // '' at root

    // Expose config to the entire app
    app.provide('appConfig', cfg);
    app.config.globalProperties.$config = cfg;

    // Safe path helper (use this in templates instead of basePath + '/x')
    app.config.globalProperties.$path = makePathJoiner(cfg.BASE_PATH);
    app.provide('$path', app.config.globalProperties.$path);

    // log the build version
    console.log(
        "%cWYSH version",
        "color: #0d6efd; font-weight: bold",
        process.env.npm_package_version
    );

    // Load persisted user state
    const userInfoStore = useUserInfoStore();
    await userInfoStore.loadState();

    // vue-router history base MUST be '/' for root, or '/foo/' for subpath
    const routerBaseForHistory = (cfg.BASE_PATH || '/')  // '' -> '/'
    const router = buildRouter(routerBaseForHistory);

    app.use(router);

    // Router guards
    router.beforeEach((to, from, next) => {
        const loggedIn = !!userInfoStore.userId;
        if (to.meta.requiresAuth && !loggedIn) next('/login');
        else next();
    });

    router.onError((err) => {
        console.error("[router.onError]", err);
    });

    router.afterEach((to, from, failure) => {
        console.log("[router.afterEach]", {
            from: from.fullPath,
            to: to.fullPath,
            toName: to.name,
            failure
        });
        console.log("[router.afterEach] currentRoute =", router.currentRoute.value.fullPath);
    });

    // Mount the app
    app.mount('#app');
})();
